/**
 * Catálogo de países: ISO 3166-1 alfa-2, prefijo telefónico y nombre en español.
 *
 * ── Por qué vive en `shared/` y no en `web/` ──
 * Lo necesitan la web Y la app móvil, y **es dato, no componente**: la regla de
 * que los componentes de la web no cruzan a móvil (CLAUDE.md) no aplica acá.
 * Mantener 250 prefijos telefónicos en dos archivos es garantizar que un día
 * difieran — y el que difiera va a ser el que nadie mire.
 *
 * Lo que NO cruza son los componentes que lo pintan: `CamposPais.tsx` en la web y
 * `CampoTelefono.tsx` en móvil, cada uno con sus piezas nativas.
 *
 * ── Por qué el nombre NO está escrito acá ──
 * Escribir 250 nombres a mano es 250 oportunidades de una tilde mal puesta, y
 * además queda congelado: los países se renombran (Turquía → Türkiye, Suazilandia
 * → Esuatini) y nadie se acuerda de venir a editarlo. `Intl.DisplayNames` los
 * saca del sistema, ya traducidos y al día, y es parte del navegador — no una
 * dependencia.
 *
 * Si `Intl.DisplayNames` no estuviera —navegador viejo, o Hermes en React Native
 * compilado sin `Intl`—, se muestra el código en mayúscula. Se degrada, no se
 * rompe.
 *
 * ⚠️ **Lo que sí está escrito acá es el prefijo telefónico**, porque no se puede
 * derivar de nada: no hay una tabla estándar en el navegador que lo dé.
 */

/**
 * `iso:prefijo`, en una sola cadena en vez de 250 objetos.
 *
 * Es la lista completa de la UIT. Un objeto por país serían 250 líneas de ruido
 * para un dato que se lee de corrido; así entra en una pantalla y se revisa de un
 * vistazo. Se parsea una sola vez al cargar el módulo.
 */
const TABLA =
  'af:93 al:355 de:49 dz:213 ad:376 ao:244 ai:1264 ag:1268 sa:966 ar:54 am:374 aw:297 au:61 ' +
  'at:43 az:994 bs:1242 bd:880 bb:1246 bh:973 be:32 bz:501 bj:229 bm:1441 by:375 bo:591 ' +
  'ba:387 bw:267 br:55 bn:673 bg:359 bf:226 bi:257 bt:975 cv:238 kh:855 cm:237 ca:1 qa:974 ' +
  'td:235 cl:56 cn:86 cy:357 co:57 km:269 cg:242 cd:243 kp:850 kr:82 ci:225 cr:506 hr:385 ' +
  'cu:53 cw:599 dk:45 dm:1767 ec:593 eg:20 sv:503 ae:971 er:291 sk:421 si:386 es:34 us:1 ' +
  'ee:372 et:251 ph:63 fi:358 fj:679 fr:33 ga:241 gm:220 ge:995 gh:233 gi:350 gd:1473 gr:30 ' +
  'gl:299 gp:590 gu:1671 gt:502 gf:594 gg:44 gn:224 gq:240 gw:245 gy:592 ht:509 hn:504 hk:852 ' +
  'hu:36 in:91 id:62 iq:964 ir:98 ie:353 bv:47 im:44 cx:61 is:354 ky:1345 cc:61 ck:682 fo:298 ' +
  'gs:500 fk:500 mp:1670 mh:692 sb:677 tc:1649 vg:1284 vi:1340 il:972 it:39 jm:1876 jp:81 ' +
  'je:44 jo:962 kz:7 ke:254 kg:996 ki:686 kw:965 la:856 ls:266 lv:371 lb:961 lr:231 ly:218 ' +
  'li:423 lt:370 lu:352 mo:853 mk:389 mg:261 my:60 mw:265 mv:960 ml:223 mt:356 ma:212 mq:596 ' +
  'mu:230 mr:222 yt:262 mx:52 fm:691 md:373 mc:377 mn:976 me:382 ms:1664 mz:258 mm:95 na:264 ' +
  'nr:674 np:977 ni:505 ne:227 ng:234 nu:683 nf:672 no:47 nc:687 nz:64 om:968 nl:31 pk:92 ' +
  'pw:680 ps:970 pa:507 pg:675 py:595 pe:51 pf:689 pl:48 pt:351 pr:1787 gb:44 cf:236 cz:420 ' +
  'do:1809 re:262 rw:250 ro:40 ru:7 eh:212 ws:685 as:1684 bl:590 kn:1869 sm:378 mf:590 ' +
  'pm:508 vc:1784 sh:290 lc:1758 st:239 sn:221 rs:381 sc:248 sl:232 sg:65 sx:1721 sy:963 ' +
  'so:252 lk:94 sz:268 za:27 sd:249 ss:211 se:46 ch:41 sr:597 sj:47 th:66 tw:886 tz:255 ' +
  'tj:992 io:246 tf:262 tl:670 tg:228 tk:690 to:676 tt:1868 tn:216 tm:993 tr:90 tv:688 ' +
  'ua:380 ug:256 uy:598 uz:998 vu:678 va:379 ve:58 vn:84 wf:681 ye:967 dj:253 zm:260 zw:263';

export type Pais = { iso: string; dial: string; nombre: string };

/**
 * Los que Copathy usa a diario van primero; el resto va alfabético por su nombre
 * traducido. Una lista de 250 países en orden alfabético puro pone Afganistán
 * arriba y Venezuela abajo del todo, y eso es scroll en cada alta.
 */
const PRIORITARIOS = ['ve', 'co', 'us', 'es', 'mx', 'pe', 'cl', 'ar', 'ec', 'pa', 'do', 'br'];

const nombrador = (() => {
  try {
    return new Intl.DisplayNames(['es'], { type: 'region' });
  } catch {
    return null;
  }
})();

/** Nombre en español del país, o su código si el navegador no sabe traducirlo. */
export function nombrePais(iso: string): string {
  const codigo = iso.toUpperCase();
  try {
    return nombrador?.of(codigo) ?? codigo;
  } catch {
    return codigo;
  }
}

const crudos: Pais[] = TABLA.split(' ')
  .filter(Boolean)
  .map((par) => {
    const [iso = '', prefijo = ''] = par.split(':');
    return { iso, dial: `+${prefijo}`, nombre: nombrePais(iso) };
  });

const comparador = new Intl.Collator('es');

export const PAISES: Pais[] = [
  ...PRIORITARIOS.map((iso) => crudos.find((p) => p.iso === iso)).filter((p): p is Pais => !!p),
  ...crudos
    .filter((p) => !PRIORITARIOS.includes(p.iso))
    .sort((a, b) => comparador.compare(a.nombre, b.nombre)),
];

export const PAIS_POR_DEFECTO = 've';
export const DIAL_POR_DEFECTO = '+58';

/**
 * Bandera desde flagcdn.
 *
 * ⚠️ Es un **host externo**, y la CSP de `helmet` lo bloquea cuando Express sirve
 * la SPA — no en el dev server de Vite, donde no hay helmet. O sea: se ve perfecto
 * en local y roto en el servidor. Por eso `flagcdn.com` está declarado en `img-src`
 * en `src/app.ts`; si se cambia de proveedor de banderas, hay que cambiarlo ahí
 * también.
 *
 * Se descartó la bandera como emoji: **en Windows no se dibuja**, sale el par de
 * letras. Y Jonathan trabaja en Windows.
 */
export const banderaUrl = (iso: string) => `https://flagcdn.com/${iso.toLowerCase()}.svg`;

/**
 * La misma bandera, en PNG, **para la app móvil**.
 *
 * ⚠️ `Image` de React Native **no dibuja SVG**: no falla con un error, deja el
 * hueco en blanco. Renderizar SVG requiere `react-native-svg`, que es una
 * dependencia nativa más, y en este proyecto ya se decidió no sumar una por un
 * detalle visual (D-081, el ojito). Un PNG lo pinta `Image` sin nada extra.
 *
 * `w40` son 40 px de ancho — el doble de los ~20 px en que se muestra, así que se
 * ve nítida en pantallas de alta densidad sin bajar un archivo grande.
 */
export const banderaPngUrl = (iso: string) => `https://flagcdn.com/w40/${iso.toLowerCase()}.png`;

/** Compone el teléfono para guardar: `+58 4141234567`. */
export const componerTelefono = (dial: string, numero: string) => {
  const limpio = numero.replace(/\D/g, '');
  return limpio ? `${dial} ${limpio}` : '';
};

/**
 * Inverso de `componerTelefono`, para poder EDITAR un teléfono ya guardado.
 *
 * ⚠️ Se prueba el prefijo **más largo primero**: `+1` es prefijo de `+1809`, y con
 * el orden al revés todos los del Caribe se leerían como Estados Unidos. Es la
 * misma corrección que ya trae el componente de Polaris; se conserva porque el
 * error vuelve solo en cuanto alguien reordene la tabla.
 */
export function descomponerTelefono(guardado: string | null): { dial: string; numero: string } {
  if (!guardado) return { dial: DIAL_POR_DEFECTO, numero: '' };
  const limpio = guardado.trim();
  const dials = [...new Set(PAISES.map((p) => p.dial))].sort((a, b) => b.length - a.length);
  for (const dial of dials) {
    if (limpio.startsWith(dial)) {
      return { dial, numero: limpio.slice(dial.length).replace(/\D/g, '') };
    }
  }
  // Sin prefijo reconocible —un teléfono cargado antes de este campo—: se deja el
  // número tal cual en vez de descartarlo.
  return { dial: DIAL_POR_DEFECTO, numero: limpio.replace(/\D/g, '') };
}
